import type { Maybe } from '@adornis/base/utilTypes.js';
import type { UserWithPermissionFields } from './UserWithPermissionFields.js';

/**
 * Produces a stable hash string from only the fields relevant for permission evaluation.
 * This ensures permission re-evaluation does not fire when unrelated user fields change.
 */
export function hashUserPermissionFields(user: Maybe<UserWithPermissionFields>): string {
  if (!user) return 'null';
  const roles = user.roles?.map(r => `${r.name}:${(r.contexts ?? []).join(',')}`).join('|') ?? '';
  const sorgeberechtigteKinder = (user.sorgeberechtigteKinderIDs ?? []).join(',');
  const spendenKinder = (user.spendenKinderIDs ?? []).join(',');
  return `${user._id}|${roles}|${sorgeberechtigteKinder}|${spendenKinder}`;
}

/**
 * Hash any payload value. For user-shaped objects (containing _id and roles), use the
 * permission-field hash. For anything else, fall back to JSON.stringify.
 */
function hashPayloadValue(value: unknown): string {
  if (value == null) return 'null';
  if (typeof value === 'object' && value !== null && '_id' in value && 'roles' in value) {
    return hashUserPermissionFields(value as UserWithPermissionFields);
  }
  return JSON.stringify(value);
}

/**
 * Hashes an ABAC payload by reducing user-shaped fields to their permission-relevant hash.
 * This prevents permission re-evaluation when fields irrelevant to permissions change.
 */
export function hashAbacPayload(payload: Record<string, unknown>): string {
  return Object.entries(payload)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${hashPayloadValue(v)}`)
    .join('&');
}
